import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { chatService, transformConversation } from "../services/chatService";
import { useChatListSocket } from "./useChatListSocket";

/**
 * 📋 CONVERSATIONS HOOK
 * Manages conversation list data with real-time updates and pagination
 */
export const useConversations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false,
  });

  // 🎯 QUERY PARAMS: Get active tab from URL, default to 'escalated'
  const getActiveTabFromURL = () => {
    const tab = searchParams.get("tab");
    return [
      "escalated",
      "assigned",
      "active",
      "resolved",
      "disable",
      "all",
    ].includes(tab)
      ? tab
      : "escalated";
  };

  const [filters, setFilters] = useState({
    status: getActiveTabFromURL(),
    channelType: null,
    search: "",
  });
  const [stats, setStats] = useState({
    active: 0,
    escalated: 0,
    assigned: 0,
    resolved: 0,
    // 🎯 BADGE FIELDS: Required for real-time badge updates
    pending_escalations: 0, // Badge for "escalated" tab
    assigned_with_unread: 0, // Badge for "assigned" tab
  });

  // Track current selected conversation for unread count logic
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const isInitialLoad = useRef(true);

  // 🎯 QUERY PARAMS: Sync URL changes with filter state
  useEffect(() => {
    const currentTab = getActiveTabFromURL();
    if (filters.status !== currentTab) {
      console.log("🔄 [URLSync] Syncing filter state with URL:", currentTab);
      setFilters((prev) => ({ ...prev, status: currentTab }));
    }
  }, [searchParams, filters.status]);

  /**
   * 📊 Refresh stats from API
   */
  const refreshStats = useCallback(async () => {
    try {
      const summary = await chatService.getSummary();
      setStats(summary.stats || summary);
    } catch (error) {
      console.error("Error refreshing stats:", error);
    }
  }, []);

  /**
   * 🔍 Update filters and sync with URL query params
   */
  const updateFilters = useCallback(
    (newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
      setPagination((prev) => ({ ...prev, page: 1 }));

      // 🎯 QUERY PARAMS: Update URL when tab status changes
      if (newFilters.status) {
        console.log(
          "🔄 [TabSwitch] Updating URL query param:",
          newFilters.status
        );
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", newFilters.status);
          return newParams;
        });
      }
    },
    [setSearchParams]
  );

  /**
   * �🔄 Real-time event handlers
   */
  const handleNewConversation = useCallback(
    (eventData) => {
      console.log("📥 New conversation received:", eventData);

      // Prefer data.conversation if available (from socket event structure)
      const conversationData =
        eventData.conversation ||
        (eventData.data && eventData.data.conversation);
      if (!conversationData) {
        console.warn(
          "[handleNewConversation] No conversation data found in eventData",
          eventData
        );
        return;
      }

      const newConversation = transformConversation(conversationData);

      // ✅ TRADITIONAL PAGINATION: Only add if on page 1 and matches filter
      if (
        pagination.page === 1 &&
        (filters.status === "all" || newConversation.status === filters.status)
      ) {
        console.log(
          "➕ [NewConv] Adding new conversation to page 1:",
          newConversation.id
        );

        setConversations((prev) => {
          // Check if conversation already exists
          const existingIndex = prev.findIndex(
            (conv) => conv.id === newConversation.id
          );

          if (existingIndex !== -1) {
            console.log(
              "🔄 [Dedup] Updating existing conversation with complete data:",
              newConversation.id
            );

            const updatedConversations = [...prev];
            updatedConversations[existingIndex] = {
              ...updatedConversations[existingIndex],
              ...newConversation,
              last_message:
                updatedConversations[existingIndex].last_message ||
                newConversation.last_message,
              last_message_at:
                updatedConversations[existingIndex].last_message_at ||
                newConversation.last_message_at,
              unread_count:
                updatedConversations[existingIndex].unread_count ||
                newConversation.unread_count,
            };

            return updatedConversations.sort((a, b) => {
              if (a.is_pinned && !b.is_pinned) return -1;
              if (!a.is_pinned && b.is_pinned) return 1;
              return new Date(b.last_message_at) - new Date(a.last_message_at);
            });
          }

          // Add new conversation and re-sort immediately
          const updatedConversations = [newConversation, ...prev];
          return updatedConversations.sort((a, b) => {
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            return new Date(b.last_message_at) - new Date(a.last_message_at);
          });
        });
      } else {
        console.log("ℹ️ [NewConv] Not adding to current page:", {
          currentPage: pagination.page,
          status: newConversation.status,
          activeFilter: filters.status,
          reason: pagination.page !== 1 ? "Not on page 1" : "Filter mismatch",
        });
      }

      // Update stats
      setStats((prev) => ({
        ...prev,
        active: prev.active + 1,
      }));
    },
    [pagination.page, filters.status]
  );

  const handleNewMessage = useCallback(
    (eventData) => {
      console.log("📥 New message for conversation list:", eventData);

      setConversations((prev) => {
        // ✅ TRADITIONAL PAGINATION: Only update if conversation exists in current page
        const existingConv = prev.find(
          (conv) => conv.id === eventData.conversationId
        );

        if (existingConv) {
          console.log(
            "🔄 [MessageUpdate] Updating existing conversation in current page:",
            {
              conversationId: eventData.conversationId,
              currentPage: pagination.page,
              currentStatus: existingConv.status,
              activeTab: filters.status,
            }
          );

          // Update existing conversation with new message
          const updatedConversations = prev.map((conv) => {
            if (conv.id === eventData.conversationId) {
              return {
                ...conv,
                last_message: eventData.message,
                last_message_at:
                  eventData.timestamp ||
                  eventData.time ||
                  new Date().toISOString(),
                last_message_media_type: eventData.media_type || "text",
                // ✅ ALWAYS use unread_count from socket (backend handles all logic)
                // No auto-read on select - only explicit actions clear unread:
                // 1. Operator reply (backend sets to 0)
                // 2. Manual "Mark as Read" button
                unread_count: eventData.unread_count ?? conv.unread_count,
                // Update last activity
                updated_at: new Date().toISOString(),
              };
            }
            return conv;
          });

          // 🎯 RECALCULATE BADGE: Count conversations with unread > 0
          // No manual arithmetic - derived from actual data
          const escalatedWithUnread = updatedConversations.filter(
            (c) => c.status === "escalated" && c.unread_count > 0
          ).length;

          const assignedWithUnread = updatedConversations.filter(
            (c) => c.status === "assigned" && c.unread_count > 0
          ).length;

          console.log("📊 [Badge] Recalculated from conversations:", {
            escalated: escalatedWithUnread,
            assigned: assignedWithUnread,
            totalConversations: updatedConversations.length,
          });

          // Update stats with calculated badge counts
          setStats((prevStats) => ({
            ...prevStats,
            pending_escalations: escalatedWithUnread,
            assigned_with_unread: assignedWithUnread,
          }));

          // Re-sort to move updated conversation to top (WhatsApp behavior)
          return updatedConversations.sort((a, b) => {
            // Pinned conversations first
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;
            // Then by last message time (newest first)
            return new Date(b.last_message_at) - new Date(a.last_message_at);
          });
        } else {
          console.log(
            "ℹ️ [MessageUpdate] Conversation not in current page, ignoring update:",
            {
              conversationId: eventData.conversationId,
              currentPage: pagination.page,
              customerName: eventData.customerName,
            }
          );

          // ✅ TRADITIONAL PAGINATION: Don't create conversations from real-time events
          // They will be loaded when user navigates to the appropriate page
          return prev;
        }
      });
    },
    [filters.status, pagination.page]
  );

  const handleConversationUpdated = useCallback(
    (eventData) => {
      console.log("📥 Conversation updated (auto-escalation):", eventData);

      let conversationFound = false;
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === eventData.conversationId) {
            conversationFound = true;
            return {
              ...conv,
              status: eventData.status,
              escalation_reason: eventData.escalation_reason,
              last_message: eventData.last_message || conv.last_message,
              updated_at: eventData.updated_at,
            };
          }
          return conv;
        })
      );

      // 🚀 Auto-switch to escalated tab when conversation is escalated
      if (conversationFound && eventData.status === "escalated") {
        console.log(
          "🔄 [AutoSwitch] Auto-escalation detected, switching to 'escalated' tab"
        );
        updateFilters({ status: "escalated" });

        // 📈 Increment escalated count immediately for better UX
        setStats((prev) => ({
          ...prev,
          escalated: (prev.escalated || 0) + 1,
        }));
      }

      // Update stats when status changes (background refresh)
      refreshStats();
    },
    [refreshStats, updateFilters]
  );

  const handleConversationAssigned = useCallback(
    (eventData) => {
      console.log("📥 Conversation assigned:", eventData);

      let conversationFound = false;
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === eventData.conversationId) {
            conversationFound = true;
            return {
              ...conv,
              status: eventData.status,
              assigned_to: eventData.assignedTo,
              updated_at: new Date().toISOString(),
            };
          }
          return conv;
        })
      );

      // 🎯 IMMEDIATE BADGE INCREMENT + AUTO-SWITCH: Update badge and switch tab for manual assignment
      if (conversationFound) {
        console.log("� [AutoSwitch] Manual assignment completed");

        // 🔄 AUTO-SWITCH: Switch to assigned tab after manual assignment
        console.log(
          "� [AutoSwitch] Switching to 'assigned' tab after conversation assignment"
        );
        updateFilters({ status: "assigned" });

        // �🔥 SUCCESS FEEDBACK: Show success message with auto-switch
        toast.success(`Conversation assigned to ${eventData.assignedTo}`, {
          duration: 3000,
          position: "top-right",
        });
      } else {
        console.warn(
          "⚠️ [ManualAssign] Conversation not found for assignment:",
          eventData.conversationId
        );
      }

      // Keep async refresh for eventual consistency
      refreshStats();
    },
    [refreshStats, updateFilters]
  );

  const handleConversationResolved = useCallback(
    (eventData) => {
      console.log("📥 Conversation resolved:", eventData);

      let conversationFound = false;
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === eventData.conversationId) {
            conversationFound = true;
            return {
              ...conv,
              status: eventData.status,
              resolved_at: eventData.resolved_at,
              assigned_to: null, // Clear assignment when resolved
              updated_at: new Date().toISOString(),
            };
          }
          return conv;
        })
      );

      // ✅ NO AUTO-SWITCH: Keep user in current tab
      if (conversationFound) {
        // Show success feedback
        toast.success(`Conversation resolved and returned to AI`, {
          duration: 3000,
          position: "top-right",
        });
      } else {
        console.warn(
          "⚠️ [AutoSwitch] Conversation not found for resolution:",
          eventData.conversationId
        );
      }

      refreshStats();
    },
    [refreshStats]
  );

  /**
   * 🚫 Handle conversation disabled via socket
   */
  const handleConversationDisabled = useCallback(
    (eventData) => {
      console.log("📥 Conversation disabled:", eventData);

      let conversationFound = false;
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === eventData.conversationId) {
            conversationFound = true;
            return {
              ...conv,
              status: "disable",
              disabled_at: eventData.disabled_at,
              updated_at: new Date().toISOString(),
            };
          }
          return conv;
        })
      );

      // ✅ NO AUTO-SWITCH: Keep user in current tab
      if (conversationFound) {
        // Show success feedback
        toast.success(`Conversation has been disabled`, {
          duration: 3000,
          icon: "🚫",
        });
      }

      refreshStats();
    },
    [refreshStats]
  );

  // Initialize socket listeners
  const { isConnected } = useChatListSocket({
    onNewConversation: handleNewConversation,
    onNewMessage: handleNewMessage,
    onConversationUpdated: handleConversationUpdated,
    onConversationAssigned: handleConversationAssigned,
    onConversationResolved: handleConversationResolved,
    onConversationDisabled: handleConversationDisabled,
  });

  /**
   * 📄 Load conversations from API (Infinite Scroll Pagination)
   */
  const loadConversations = useCallback(
    async (isLoadMore = false) => {
      try {
        // Set appropriate loading state
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const params = {
          page: pagination.page,
          limit: pagination.limit,
          status: filters.status !== "all" ? filters.status : undefined,
          channelType:
            filters.channelType !== "all" ? filters.channelType : undefined,
        };

        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (
            params[key] === undefined ||
            params[key] === null ||
            params[key] === ""
          ) {
            delete params[key];
          }
        });

        console.log("🔄 Loading conversations with params:", params);
        console.log(
          "📄 [DEBUG] Loading page:",
          pagination.page,
          "isLoadMore:",
          isLoadMore
        );

        const response = await chatService.getConversations(params);

        const transformedConversations = response.conversations.map(
          transformConversation
        );

        console.log(
          "📥 [DEBUG] Loaded",
          transformedConversations.length,
          "conversations for page",
          pagination.page
        );

        // ✅ INFINITE SCROLL: Replace for page 1, append for page > 1
        if (pagination.page === 1) {
          console.log("🔄 [DEBUG] Replacing data for page 1");
          setConversations(transformedConversations);
        } else {
          console.log("➕ [DEBUG] Appending data for page", pagination.page);
          setConversations((prev) => {
            // Deduplication: filter out conversations that already exist
            const existingIds = new Set(prev.map((conv) => conv.id));
            const newConversations = transformedConversations.filter(
              (conv) => !existingIds.has(conv.id)
            );

            console.log(
              "📊 [DEBUG] Deduplication:",
              transformedConversations.length,
              "received,",
              newConversations.length,
              "new"
            );

            return [...prev, ...newConversations];
          });
        }

        // ✅ UPDATE PAGINATION METADATA
        setPagination((prev) => ({
          ...prev,
          limit: response.limit || prev.limit,
          total: response.total || 0,
          hasMore:
            pagination.page * (response.limit || prev.limit) <
            (response.total || 0),
        }));

        if (response.stats) {
          setStats(response.stats);
        }
      } catch (error) {
        console.error("Error loading conversations:", error);

        // Enhanced error handling
        let errorMessage = "Failed to load conversations";
        if (error.code === "NETWORK_ERROR") {
          errorMessage = "Network error. Please check your connection.";
        } else if (error.code === "TIMEOUT") {
          errorMessage = "Request timeout. Please try again.";
        } else if (error.response?.status === 500) {
          errorMessage = "Server error. Please try again later.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        setError(errorMessage);
      } finally {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [pagination.page, pagination.limit, filters.status, filters.channelType]
  );

  /**
   * � Optimistic update for outgoing messages (WhatsApp-like immediate UI update)
   */
  const handleOptimisticMessage = useCallback(
    (conversationId, message, senderType = "user") => {
      console.log("⚡ Optimistic message update:", {
        conversationId,
        message,
        senderType,
      });

      setConversations((prev) => {
        const updatedConversations = prev.map((conv) => {
          if (conv.id === conversationId) {
            return {
              ...conv,
              last_message: message,
              last_message_at: new Date().toISOString(),
              last_message_media_type: "text",
              updated_at: new Date().toISOString(),
            };
          }
          return conv;
        });

        // Re-sort to move conversation to top immediately
        return updatedConversations.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return new Date(b.last_message_at) - new Date(a.last_message_at);
        });
      });
    },
    []
  );

  /**
   * 🔄 Load more conversations (infinite scroll pagination)
   */
  const loadMore = useCallback(async () => {
    if (loadingMore || !pagination.hasMore) {
      console.log("⚠️ [loadMore] Cannot load more:", {
        loadingMore,
        hasMore: pagination.hasMore,
      });
      return;
    }

    try {
      const nextPage = pagination.page + 1;
      console.log(
        "🔄 [loadMore] Loading page",
        nextPage,
        "with",
        pagination.limit,
        "items"
      );

      setLoadingMore(true);
      setError(null);

      const params = {
        page: nextPage,
        limit: pagination.limit,
        status: filters.status !== "all" ? filters.status : undefined,
        channelType:
          filters.channelType !== "all" ? filters.channelType : undefined,
      };

      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      const response = await chatService.getConversations(params);
      const transformedConversations = response.conversations.map(
        transformConversation
      );

      console.log(
        "📥 [loadMore] Loaded",
        transformedConversations.length,
        "conversations for page",
        nextPage
      );

      // Append new conversations with deduplication
      setConversations((prev) => {
        const existingIds = new Set(prev.map((conv) => conv.id));
        const newConversations = transformedConversations.filter(
          (conv) => !existingIds.has(conv.id)
        );

        console.log(
          "📊 [loadMore] Deduplication:",
          transformedConversations.length,
          "received,",
          newConversations.length,
          "new"
        );

        return [...prev, ...newConversations];
      });

      // Update pagination metadata
      setPagination((prev) => ({
        ...prev,
        page: nextPage,
        total: response.total || prev.total,
        hasMore:
          nextPage * (response.limit || prev.limit) <
          (response.total || prev.total),
      }));
    } catch (error) {
      console.error("Error in loadMore:", error);
      setError("Failed to load more conversations");
    } finally {
      setLoadingMore(false);
    }
  }, [
    loadingMore,
    pagination.hasMore,
    pagination.page,
    pagination.limit,
    filters.status,
    filters.channelType,
  ]);

  /**
   * 📌 Select conversation (for UI highlighting only)
   */
  const selectConversation = useCallback((conversationId) => {
    // ✅ ONLY update selected conversation ID for UI state
    // NO auto-read, NO badge changes
    // Unread only cleared by:
    // 1. Operator reply (backend)
    // 2. Manual "Mark as Read" button
    setSelectedConversationId(conversationId);

    console.log("� [SelectConv] Selected conversation:", conversationId);
  }, []);

  /**
   * 📖 Mark conversation as read
   */
  const handleMarkAsRead = useCallback(
    async (conversationId) => {
      try {
        // 1. Validate operator name
        const operatorName = localStorage.getItem("operatorName");
        if (!operatorName) {
          toast.error("Please set up your operator name first");
          return;
        }

        // 2. Get conversation data before update (for revert on error)
        const previousConversation = conversations.find(
          (c) => c.id === conversationId
        );
        const previousUnreadCount = previousConversation?.unread_count || 0;

        // Only proceed if there's unread count
        if (previousUnreadCount === 0) {
          console.log(
            "📖 [MarkAsRead] Conversation already read:",
            conversationId
          );
          return;
        }

        console.log("📖 [MarkAsRead] Marking conversation as read:", {
          conversationId,
          previousUnreadCount,
          status: previousConversation?.status,
        });

        // 3. Optimistic update - Update unread_count immediately
        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === conversationId) {
              return { ...conv, unread_count: 0 };
            }
            return conv;
          })
        );

        // 4. Decrement badge stats immediately based on status
        setStats((prev) => {
          if (previousConversation?.status === "escalated") {
            const newEscalatedCount = Math.max(
              (prev.pending_escalations || 0) - 1,
              0
            );
            console.log(
              "📉 [Badge] Escalated badge decrement (mark as read):",
              prev.pending_escalations,
              "→",
              newEscalatedCount
            );
            return {
              ...prev,
              pending_escalations: newEscalatedCount,
            };
          } else if (previousConversation?.status === "assigned") {
            const newAssignedCount = Math.max(
              (prev.assigned_with_unread || 0) - 1,
              0
            );
            console.log(
              "📉 [Badge] Assigned badge decrement (mark as read):",
              prev.assigned_with_unread,
              "→",
              newAssignedCount
            );
            return {
              ...prev,
              assigned_with_unread: newAssignedCount,
            };
          }
          return prev;
        });

        // 5. Call API
        const response = await chatService.markAsRead(
          conversationId,
          operatorName
        );

        // 6. Show success toast with API message
        toast.success(response.message || "Conversation marked as read");

        console.log("✅ [MarkAsRead] Success:", response);
      } catch (error) {
        console.error("❌ [MarkAsRead] Error:", error);

        // Get conversation data for revert
        const previousConversation = conversations.find(
          (c) => c.id === conversationId
        );
        const previousUnreadCount = previousConversation?.unread_count || 0;

        // Revert optimistic update on error
        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === conversationId) {
              return { ...conv, unread_count: previousUnreadCount };
            }
            return conv;
          })
        );

        // Revert badge decrement on error
        setStats((prev) => {
          if (previousConversation?.status === "escalated") {
            const revertedCount = (prev.pending_escalations || 0) + 1;
            console.log(
              "🔄 [Badge] Reverting escalated badge:",
              prev.pending_escalations,
              "→",
              revertedCount
            );
            return {
              ...prev,
              pending_escalations: revertedCount,
            };
          } else if (previousConversation?.status === "assigned") {
            const revertedCount = (prev.assigned_with_unread || 0) + 1;
            console.log(
              "🔄 [Badge] Reverting assigned badge:",
              prev.assigned_with_unread,
              "→",
              revertedCount
            );
            return {
              ...prev,
              assigned_with_unread: revertedCount,
            };
          }
          return prev;
        });

        // Show error toast
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to mark conversation as read";
        toast.error(errorMessage);
      }
    },
    [conversations]
  );

  /**
   * 🔄 Refresh conversations (reset to page 1)
   */
  const refresh = useCallback(async () => {
    console.log("🔄 [refresh] Manual refresh triggered");

    // Reset pagination to page 1
    setPagination((prev) => ({ ...prev, page: 1 }));

    // Clear existing conversations for immediate feedback
    setConversations([]);

    // Call loadConversations directly to avoid useEffect dependency issues
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: 1, // Force page 1 for refresh
        limit: pagination.limit,
        status: filters.status !== "all" ? filters.status : undefined,
        channelType:
          filters.channelType !== "all" ? filters.channelType : undefined,
      };

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === undefined ||
          params[key] === null ||
          params[key] === ""
        ) {
          delete params[key];
        }
      });

      console.log("🔄 [refresh] Loading conversations with params:", params);

      const response = await chatService.getConversations(params);
      const transformedConversations = response.conversations.map(
        transformConversation
      );

      console.log(
        "📥 [refresh] Loaded",
        transformedConversations.length,
        "conversations"
      );

      // Update conversations with fresh data
      setConversations(transformedConversations);

      // Update pagination metadata
      setPagination((prev) => ({
        ...prev,
        page: 1,
        limit: response.limit || prev.limit,
        total: response.total || 0,
        hasMore: 1 * (response.limit || prev.limit) < (response.total || 0),
      }));

      // Update stats if available
      if (response.stats) {
        setStats(response.stats);
      }

      // Also refresh stats separately
      await refreshStats();
    } catch (error) {
      console.error("Error in manual refresh:", error);

      let errorMessage = "Failed to refresh conversations";
      if (error.code === "NETWORK_ERROR") {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, filters.status, filters.channelType, refreshStats]);

  // Initial load - wait for socket connection
  useEffect(() => {
    if (isInitialLoad.current && isConnected) {
      console.log(
        "🚀 [useConversations] Initial load triggered, socket connected:",
        isConnected
      );

      const initialLoad = async () => {
        try {
          setLoading(true);
          setError(null);

          const params = {
            page: 1,
            limit: pagination.limit,
            status: filters.status !== "all" ? filters.status : undefined,
            channelType:
              filters.channelType !== "all" ? filters.channelType : undefined,
          };

          Object.keys(params).forEach((key) => {
            if (
              params[key] === undefined ||
              params[key] === null ||
              params[key] === ""
            ) {
              delete params[key];
            }
          });

          console.log(
            "🚀 [initialLoad] Loading conversations with params:",
            params
          );

          const response = await chatService.getConversations(params);
          const transformedConversations = response.conversations.map(
            transformConversation
          );

          setConversations(transformedConversations);

          setPagination((prev) => ({
            ...prev,
            page: 1,
            total: response.total || 0,
            hasMore: 1 * (response.limit || prev.limit) < (response.total || 0),
          }));

          if (response.stats) {
            setStats(response.stats);
          }
        } catch (error) {
          console.error("Error in initial load:", error);
          setError("Failed to load conversations");
        } finally {
          setLoading(false);
        }
      };

      initialLoad();
      isInitialLoad.current = false;
    }
  }, [isConnected]); // Only depend on socket connection

  // Load conversations when page changes (for loadMore functionality)
  useEffect(() => {
    // Only trigger for page > 1 (loadMore scenarios)
    // Page 1 is handled by initial load and manual refresh
    if (pagination.page > 1 && !loading && !isInitialLoad.current) {
      console.log(
        "🚀 [useEffect] Loading more conversations for page:",
        pagination.page
      );
      loadConversations(true); // isLoadMore = true
    }
  }, [pagination.page]); // Minimal dependencies to avoid loops

  // Reload when filters change (reset to page 1 and reload)
  useEffect(() => {
    if (!isInitialLoad.current) {
      console.log("🔍 [useEffect] Filters changed, reloading data:", {
        status: filters.status,
        channelType: filters.channelType,
      });

      // Reset pagination and reload conversations
      setPagination((prev) => ({ ...prev, page: 1 }));
      setConversations([]);

      // Call loadConversations directly to avoid dependency loops
      const loadFilteredConversations = async () => {
        try {
          setLoading(true);
          setError(null);

          const params = {
            page: 1,
            limit: pagination.limit,
            status: filters.status !== "all" ? filters.status : undefined,
            channelType:
              filters.channelType !== "all" ? filters.channelType : undefined,
          };

          Object.keys(params).forEach((key) => {
            if (
              params[key] === undefined ||
              params[key] === null ||
              params[key] === ""
            ) {
              delete params[key];
            }
          });

          console.log(
            "🔍 [filterChange] Loading conversations with new filters:",
            params
          );

          const response = await chatService.getConversations(params);
          const transformedConversations = response.conversations.map(
            transformConversation
          );

          setConversations(transformedConversations);

          setPagination((prev) => ({
            ...prev,
            page: 1,
            total: response.total || 0,
            hasMore: 1 * (response.limit || prev.limit) < (response.total || 0),
          }));

          if (response.stats) {
            setStats(response.stats);
          }
        } catch (error) {
          console.error(
            "Error loading conversations after filter change:",
            error
          );
          setError("Failed to load conversations");
        } finally {
          setLoading(false);
        }
      };

      loadFilteredConversations();
    }
  }, [filters.status, filters.channelType]); // Only filter dependencies

  return {
    // Data
    conversations,
    stats,
    pagination,
    filters,

    // State
    loading,
    loadingMore,
    error,
    isConnected,
    selectedConversationId,

    // Actions
    loadMore,
    updateFilters,
    selectConversation,
    refresh,
    handleOptimisticMessage,
    handleMarkAsRead,

    // Utils
    totalConversations: conversations.length,
  };
};
